import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

export type TextSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
export type TextWeight = 'normal' | 'semibold' | 'bold';
export type TextAlign = 'left' | 'center' | 'right';
export type TextColor = 'primary' | 'secondary' | 'accent' | 'success' | 'error' | 'warning' | 'onSurface' | 'onSurfaceVariant';

interface TextProps {
  as?: 'p' | 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  size?: TextSize;
  weight?: TextWeight;
  align?: TextAlign;
  color?: TextColor;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

export const Text: React.FC<TextProps> = ({
  as: Component = 'p',
  size = 'base',
  weight = 'normal',
  align = 'left',
  color = 'onSurface',
  className = '',
  style = {},
  children,
}) => {
  const { theme } = useTheme();

  // Get color from theme
  const getColor = (): string => {
    switch (color) {
      case 'primary':
        return theme.colors.textPrimary;
      case 'secondary':
        return theme.colors.textSecondary;
      case 'accent':
        return theme.colors.accent;
      case 'success':
        return theme.colors.success;
      case 'error':
        return theme.colors.error;
      case 'warning':
        return theme.colors.warning;
      case 'onSurface':
        return theme.colors.onSurface;
      case 'onSurfaceVariant':
        return theme.colors.onSurfaceVariant;
      default:
        return theme.colors.onSurface;
    }
  };

  // Text alignment classes
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const combinedStyle = {
    color: getColor(),
    ...style,
  };

  return (
    <Component
      className={`${theme.typography.sizes[size]} ${theme.typography.weights[weight]} ${alignClasses[align]} ${className}`}
      style={combinedStyle}
    >
      {children}
    </Component>
  );
};
